import { Pipe, PipeTransform } from '@angular/core';

/**
 * Evaluates a field's `conditionalShowConditions` against the current
 * `requestPayload` and returns `true` when the field should be visible.
 *
 * If `isConditionalShow` is false or there are no conditions the field is
 * always shown (returns `true`).
 *
 * Conditions are linked by the `logicalOperator` of the PREVIOUS row:
 *   condition[0].logicalOperator → connects condition[0] to condition[1]
 *   condition[1].logicalOperator → connects condition[1] to condition[2]
 *   …
 *
 * We evaluate left-to-right, folding with AND / OR, short-circuiting where
 * possible.  The last condition's logicalOperator is irrelevant (no next item).
 */
@Pipe({
  name: 'checkConditionalShow',
  pure: false
})
export class CheckConditionalShowPipe implements PipeTransform {

  transform(value: any, field: any, requestPayload: any): boolean {
    // Guard: if feature is not enabled, always show
    if (!field || !field.isConditionalShow) {
      return true;
    }
    const conditions: any[] = field.conditionalShowConditions;
    if (!conditions || conditions.length === 0) {
      return true;
    }
    if (!requestPayload) {
      // console.log('checkConditionalShow: requestPayload is missing for field:', field.bindKey);
      return false;
    }

    // Evaluate each condition
    const results: boolean[] = conditions.map(cond => this.evaluateCondition(cond, requestPayload));

    // Fold results left-to-right using logicalOperator of the previous condition
    // conditions[i].logicalOperator tells us how to combine result[i] with result[i+1]
    let accumulated = results[0];
    for (let i = 0; i < conditions.length - 1; i++) {
      const op = (conditions[i].logicalOperator || 'AND').toUpperCase();
      if (op === 'AND') {
        accumulated = accumulated && results[i + 1];
      } else {
        accumulated = accumulated || results[i + 1];
      }
    }
    // console.log(`checkConditionalShow [${field.bindKey}]:`, {
    //   conditions,
    //   payloadKeys: Object.keys(requestPayload),
    //   results,
    //   accumulated
    // });
    return accumulated;
  }

  // ---------------------------------------------------------------------------
  // Single condition evaluation
  // ---------------------------------------------------------------------------

  private evaluateCondition(cond: any, requestPayload: any): boolean {
    if (!cond || (!cond.fieldBindKey && !cond.fieldComponentId) || !cond.operator) {
      return true; // incomplete condition → don't hide the field
    }

    let rawValue: any;
    if (requestPayload && typeof requestPayload.get === 'function') {
      rawValue = requestPayload.get(cond.fieldBindKey);
      if (rawValue === undefined || rawValue === null) {
        rawValue = requestPayload.get(cond.fieldComponentId);
      }
    }
    if (rawValue === undefined || rawValue === null) {
      rawValue = requestPayload[cond.fieldBindKey];
    }
    if (rawValue === undefined || rawValue === null) {
      rawValue = requestPayload[cond.fieldComponentId];
    }

    const fieldType: string = (cond.fieldType || (cond._parentField ? cond._parentField.type : '') || '').toLowerCase();

    if (this.isChoiceType(fieldType)) {
      return this.evaluateChoice(cond.operator, rawValue, cond.value, cond._parentField);
    } else if (fieldType === 'date') {
      return this.evaluateDate(cond.operator, rawValue, cond.value, cond.valueFrom, cond.valueTo);
    } else if (this.isNumericType(fieldType)) {
      return this.evaluateNumeric(cond.operator, rawValue, cond.value);
    } else if (this.isTextType(fieldType)) {
      return this.evaluateText(cond.operator, rawValue, cond.value);
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Type guards
  // ---------------------------------------------------------------------------

  private isChoiceType(type: string): boolean {
    if (!type) return false;
    const t = type.toLowerCase();
    return ['checkbox', 'select', 'radio'].indexOf(t) !== -1;
  }

  private isNumericType(type: string): boolean {
    if (!type) return false;
    const t = type.toLowerCase();
    return ['number', 'currency', 'decimal'].indexOf(t) !== -1;
  }

  private isTextType(type: string): boolean {
    if (!type) return false;
    const t = type.toLowerCase();
    return ['text', 'email_address', 'textarea'].indexOf(t) !== -1;
  }

  // ---------------------------------------------------------------------------
  // Operator evaluators
  // ---------------------------------------------------------------------------

  /** Choice: equalTo / notEqualTo  (value may be array for checkbox) */
  private evaluateChoice(operator: string, rawValue: any, condValue: any, parentField?: any): boolean {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return false;
    }
    const fieldValues: any[] = Array.isArray(rawValue) ? rawValue : [rawValue];

    const options: any[] = (parentField && parentField.options && Array.isArray(parentField.options)) ? parentField.options : [];

    const getEquivalentValues = (val: any): string[] => {
      if (val === null || val === undefined) return [];
      const strVal = String(val).trim().toLowerCase();
      const equivalents = new Set<string>();
      equivalents.add(strVal);

      options.forEach(opt => {
        const optId = String(opt.id || '').trim().toLowerCase();
        let matchesOpt = (optId === strVal);
        if (!matchesOpt && opt.value) {
          if (typeof opt.value === 'string' && opt.value.trim().toLowerCase() === strVal) {
            matchesOpt = true;
          } else if (typeof opt.value === 'object') {
            Object.keys(opt.value).forEach(lang => {
              if (String(opt.value[lang] || '').trim().toLowerCase() === strVal) {
                matchesOpt = true;
              }
            });
          }
        }
        if (matchesOpt) {
          if (opt.id) equivalents.add(String(opt.id).trim().toLowerCase());
          if (opt.value) {
            if (typeof opt.value === 'string') equivalents.add(opt.value.trim().toLowerCase());
            else if (typeof opt.value === 'object') {
              Object.keys(opt.value).forEach(lang => {
                if (opt.value[lang]) equivalents.add(String(opt.value[lang]).trim().toLowerCase());
              });
            }
          }
        }
      });

      return Array.from(equivalents);
    };

    const condValues: any[] = Array.isArray(condValue) ? condValue : [condValue];
    const condEquivalents = new Set<string>();
    condValues.forEach(cv => {
      getEquivalentValues(cv).forEach(eq => condEquivalents.add(eq));
    });

    const matches = fieldValues.some(v => {
      const rawEquivalents = getEquivalentValues(v);
      return rawEquivalents.some(r => condEquivalents.has(r));
    });

    return operator === 'notEqualTo' ? !matches : matches;
  }

  /** Numeric: <, <=, >, >=, ==, != */
  private evaluateNumeric(operator: string, rawValue: any, condValue: any): boolean {
    const a = parseFloat(String(rawValue));
    const b = parseFloat(String(condValue));
    if (isNaN(a) || isNaN(b)) { return false; }
    switch (operator) {
      case 'lessThan':           return a < b;
      case 'lessThanOrEqual':    return a <= b;
      case 'greaterThan':        return a > b;
      case 'greaterThanOrEqual': return a >= b;
      case 'equalTo':            return a === b;
      case 'notEqualTo':         return a !== b;
      default:                   return true;
    }
  }

  /** Text: contains / equalTo / notEqualTo */
  private evaluateText(operator: string, rawValue: any, condValue: any): boolean {
    if (rawValue === null || rawValue === undefined) { return false; }
    const a = String(rawValue).toLowerCase();
    const b = String(condValue || '').toLowerCase();
    switch (operator) {
      case 'contains':   return a.indexOf(b) !== -1;
      case 'equalTo':    return a === b;
      case 'notEqualTo': return a !== b;
      default:           return true;
    }
  }

  /** Date: all 14 operators */
  private evaluateDate(operator: string, rawValue: any, condValue: any, valueFrom: any, valueTo: any): boolean {
    const fieldDate = this.parseDate(rawValue);
    if (!fieldDate) { return false; }

    const CURRENT_DATE_N_OPS = [
      'lt_current_plus_n', 'lte_current_plus_n',
      'gt_current_plus_n', 'gte_current_plus_n',
      'lt_current_minus_n', 'lte_current_minus_n',
      'gt_current_minus_n', 'gte_current_minus_n'
    ];

    if (CURRENT_DATE_N_OPS.indexOf(operator) !== -1) {
      const n = parseInt(String(condValue), 10);
      if (isNaN(n)) { return false; }
      const today = this.stripTime(new Date());
      const offsetMs = n * 24 * 60 * 60 * 1000;
      let targetDate: Date;
      if (operator.indexOf('plus') !== -1) {
        targetDate = new Date(today.getTime() + offsetMs);
      } else {
        targetDate = new Date(today.getTime() - offsetMs);
      }
      const f = fieldDate.getTime();
      const t = targetDate.getTime();
      if (operator.startsWith('lt_current')) { return operator.indexOf('lte') !== -1 ? f <= t : f < t; }
      if (operator.startsWith('gt_current')) { return operator.indexOf('gte') !== -1 ? f >= t : f > t; }
      return false;
    }

    if (operator === 'range') {
      const from = this.parseDate(valueFrom);
      const to   = this.parseDate(valueTo);
      if (!from || !to) { return false; }
      return fieldDate >= from && fieldDate <= to;
    }

    const cmpDate = this.parseDate(condValue);
    if (!cmpDate) { return false; }
    const f = fieldDate.getTime();
    const c = cmpDate.getTime();
    switch (operator) {
      case 'lessThan':           return f < c;
      case 'lessThanOrEqual':    return f <= c;
      case 'greaterThan':        return f > c;
      case 'greaterThanOrEqual': return f >= c;
      case 'equalTo':            return f === c;
      default:                   return true;
    }
  }

  private parseDate(val: any): Date | null {
    if (!val) { return null; }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : this.stripTime(d);
  }

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
}
